import { useMemo, useState } from "react";
import { CheckCircle2, XCircle, Save, DollarSign } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getApiErrorMessage } from "@/lib/apiClient";

import { useProjetos } from "@/features/projetos/hooks/useProjetos";
import { useTimesheetPendentes, useCustosHora, useTimesheetMutations } from "@/features/projetos/hooks/useTimesheet";
import { useUsuarios } from "@/features/usuarios/hooks/useUsuarios";

const TODOS = "0";
const fmtData = (d: string | null) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");

export default function AprovacaoTimesheet() {
  const [projetoFiltro, setProjetoFiltro] = useState(TODOS);
  const { data: projetos = [] } = useProjetos();
  const { data: pendentes = [] } = useTimesheetPendentes(Number(projetoFiltro));
  const { data: custos = [] } = useCustosHora();
  const { query: usuariosQuery } = useUsuarios();
  const usuarios = usuariosQuery.data?.itens ?? [];
  const { aprovar, rejeitar, salvarCustoHora } = useTimesheetMutations();

  const nomeProjeto = (id: number) => projetos.find(p => p.id === id)?.nome ?? `#${id}`;
  const nomeFunc = (id: number) => usuarios.find(u => u.id === id)?.nome ?? `#${id}`;

  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  const toggle = (id: number) => setSelecionados(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const todosMarcados = pendentes.length > 0 && pendentes.every(p => selecionados.has(p.id));
  const toggleTodos = () => setSelecionados(todosMarcados ? new Set() : new Set(pendentes.map(p => p.id)));

  const ids = useMemo(() => [...selecionados], [selecionados]);
  const totalSelecionado = pendentes.filter(p => selecionados.has(p.id)).reduce((s, p) => s + Number(p.horas), 0);

  const aprovarSel = async () => {
    if (ids.length === 0) { toast.error("Selecione os lançamentos."); return; }
    try { await aprovar.mutateAsync(ids); toast.success(`${ids.length} aprovado(s).`); setSelecionados(new Set()); }
    catch (e) { toast.error(getApiErrorMessage(e, "Erro ao aprovar.")); }
  };
  const rejeitarSel = async () => {
    if (ids.length === 0) { toast.error("Selecione os lançamentos."); return; }
    const motivo = window.prompt("Motivo da rejeição (opcional):") ?? undefined;
    try { await rejeitar.mutateAsync({ ids, motivo }); toast.success(`${ids.length} rejeitado(s).`); setSelecionados(new Set()); }
    catch (e) { toast.error(getApiErrorMessage(e, "Erro ao rejeitar.")); }
  };

  // custo-hora editor
  const [funcCusto, setFuncCusto] = useState("");
  const [valorCusto, setValorCusto] = useState("0");
  const custoDe = (fid: number) => custos.find(c => c.funcionarioId === fid)?.custoHora ?? 0;

  const salvarCusto = async () => {
    if (!funcCusto) { toast.error("Selecione o funcionário."); return; }
    try {
      await salvarCustoHora.mutateAsync({ funcionarioId: Number(funcCusto), custoHora: Number(valorCusto) || 0 });
      toast.success("Custo-hora salvo.");
      setFuncCusto(""); setValorCusto("0");
    } catch (e) { toast.error(getApiErrorMessage(e, "Erro ao salvar custo-hora.")); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold"><CheckCircle2 className="h-6 w-6" /> Aprovação de Timesheet</h1>
        <p className="text-sm text-muted-foreground">Aprove ou rejeite os apontamentos pendentes e gerencie o custo-hora.</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base">Apontamentos pendentes</CardTitle>
          <div className="flex items-center gap-3">
            <div className="min-w-[200px]">
              <Select value={projetoFiltro} onValueChange={v => { setProjetoFiltro(v); setSelecionados(new Set()); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={TODOS}>Todos os projetos</SelectItem>
                  {projetos.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={aprovarSel} disabled={ids.length === 0}><CheckCircle2 className="mr-2 h-4 w-4" />Aprovar</Button>
            <Button variant="outline" onClick={rejeitarSel} disabled={ids.length === 0}><XCircle className="mr-2 h-4 w-4" />Rejeitar</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-2 text-sm text-muted-foreground">
            {pendentes.length} pendente(s) · {selecionados.size} selecionado(s) · {totalSelecionado.toFixed(2)} h
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-10"><Checkbox checked={todosMarcados} onCheckedChange={toggleTodos} /></TableHead>
              <TableHead>Data</TableHead><TableHead>Projeto</TableHead><TableHead>Funcionário</TableHead>
              <TableHead>Horas</TableHead><TableHead>Faturável</TableHead><TableHead>Descrição</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {pendentes.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Nenhum apontamento pendente.</TableCell></TableRow>
              ) : pendentes.map(p => (
                <TableRow key={p.id} data-state={selecionados.has(p.id) ? "selected" : undefined}>
                  <TableCell><Checkbox checked={selecionados.has(p.id)} onCheckedChange={() => toggle(p.id)} /></TableCell>
                  <TableCell>{fmtData(p.dtApontamento)}</TableCell>
                  <TableCell>{nomeProjeto(p.projetoId)}</TableCell>
                  <TableCell>{nomeFunc(p.funcionarioId)}</TableCell>
                  <TableCell className="tabular-nums">{Number(p.horas).toFixed(2)}</TableCell>
                  <TableCell>{p.faturavel ? "Sim" : "Não"}</TableCell>
                  <TableCell className="max-w-[260px] truncate">{p.descricao}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><DollarSign className="h-4 w-4" /> Custo-hora por funcionário</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px]">
              <Label>Funcionário</Label>
              <Select value={funcCusto} onValueChange={v => { setFuncCusto(v); setValorCusto(String(custoDe(Number(v)))); }}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>{usuarios.map(u => <SelectItem key={u.id} value={String(u.id)}>{u.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="w-40"><Label>Custo / hora (R$)</Label><Input type="number" step="0.01" value={valorCusto} onChange={e => setValorCusto(e.target.value)} /></div>
            <Button onClick={salvarCusto}><Save className="mr-2 h-4 w-4" />Salvar</Button>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Funcionário</TableHead><TableHead className="text-right">Custo / hora</TableHead></TableRow></TableHeader>
            <TableBody>
              {custos.length === 0 ? (
                <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground">Nenhum custo-hora cadastrado.</TableCell></TableRow>
              ) : custos.map(c => (
                <TableRow key={c.id}>
                  <TableCell>{nomeFunc(c.funcionarioId)}</TableCell>
                  <TableCell className="text-right tabular-nums">{Number(c.custoHora).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
